import React, { useState, useEffect, useRef } from "react";
import Navbar from "./Components/Navbar";
import Footer from "./Components/Footer";
import { useLanguage } from "../../contexts/LanguageContext";

import logoBSI from "../../assets/logo-bsi.png";

const useInView = (ref, threshold = 0.15) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setInView(true);
      },
      { threshold },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
};

const DonateSection = () => {
  const { language } = useLanguage();
  const ref = useRef(null);
  const inView = useInView(ref);

  const t = {
    EN: {
      tag: "Support Us",
      title: "Donation\nAccount",
      subtitle:
        "Your donation helps us continue our mission to strengthen food security across Aceh.",
      bankName: "Bank Syariah Indonesia",
      accountLabel: "Account Number",
      accountName: "Rekening Rumoh Pangan Aceh",
      copyBtn: "Copy",
      copiedBtn: "Copied!",
      note: "Every contribution, large or small, makes a real difference.",
    },
    ID: {
      tag: "Dukung Kami",
      title: "Rekening\nDonasi",
      subtitle:
        "Donasi Anda membantu kami melanjutkan misi memperkuat ketahanan pangan masyarakat Aceh.",
      bankName: "Bank Syariah Indonesia",
      accountLabel: "Nomor Rekening",
      accountName: "Rekening Rumoh Pangan Aceh",
      copyBtn: "Salin",
      copiedBtn: "Tersalin!",
      note: "Setiap kontribusi, besar maupun kecil, membuat perbedaan nyata.",
    },
  };

  const tx = t[language];
  const accountNumber = "7299665776";
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(accountNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section ref={ref} className="bg-white py-24 px-4 relative overflow-hidden">
      <div className="absolute bottom-0 right-0 font-serif text-[130px] font-bold text-[#f0ece3] leading-none select-none pointer-events-none opacity-60 translate-y-4">
        DONASI
      </div>

      <div className="container max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <div
          className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.8s ease",
          }}
        >
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#c9862a]" />
              <span className="text-[#c9862a] text-xs font-bold tracking-[4px] uppercase">
                {tx.tag}
              </span>
            </div>
            <h2 className="font-serif text-4xl md:text-6xl font-bold text-[#1a1a18] leading-tight">
              {tx.title.split("\n").map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i === 0 && <br />}
                </React.Fragment>
              ))}
            </h2>
          </div>
          <p className="text-[#6b6456] text-base leading-relaxed max-w-sm md:text-right">
            {tx.subtitle}
          </p>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-12">
          <div className="flex-1 h-px bg-[#e8e0d0]" />
          <div className="w-2 h-2 bg-[#c9862a] rotate-45" />
          <div className="flex-1 h-px bg-[#e8e0d0]" />
        </div>

        {/* Bank Card */}
        <div
          className="border border-[#e8e0d0] bg-[#f7f3ec] p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-8"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.8s ease 0.2s",
          }}
        >
          {/* BSI Icon */}
          <div className="w-36 flex items-center justify-center shrink-0">
            <img
              src={logoBSI}
              alt="BSI Logo"
              className="w-full h-full object-contain"
            />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="text-[#a09880] text-[11px] font-bold tracking-[3px] uppercase mb-1">
              {tx.accountLabel}
            </p>
            <p className="text-[#00a39e] text-xs font-bold tracking-wide mb-1">
              {tx.bankName}
            </p>
            <p className="font-serif text-3xl md:text-4xl font-bold text-[#1a1a18] tracking-widest mb-1">
              {accountNumber}
            </p>
            <p className="text-[#6b6456] text-sm">{tx.accountName}</p>
          </div>

          {/* Copy button */}
          <button
            onClick={handleCopy}
            className={`shrink-0 flex items-center gap-2 px-6 py-3 text-sm font-bold tracking-wide border transition-all duration-200 ${
              copied
                ? "bg-[#c9862a] border-[#c9862a] text-white"
                : "bg-white border-[#e8e0d0] text-[#1a1a18] hover:border-[#c9862a] hover:text-[#c9862a]"
            }`}
          >
            {copied ? tx.copiedBtn : tx.copyBtn}
          </button>
        </div>

        {/* Note */}
        <div
          className="mt-6 flex items-center gap-3"
          style={{
            opacity: inView ? 1 : 0,
            transition: "opacity 0.8s ease 0.4s",
          }}
        >
          <div className="w-2 h-2 bg-[#c9862a] rotate-45 shrink-0" />
          <p className="text-[#a09880] text-sm italic">{tx.note}</p>
        </div>
      </div>
    </section>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────────
const DonatePage = () => (
  <main className="overflow-x-hidden font-homepage bg-white">
    <Navbar />
    <DonateSection />
    <Footer />
  </main>
);

export default DonatePage;
